// =============================================================================
// client/src/components/NotificationBell.jsx | v1.1 | SC106 | The masthead bell
// =============================================================================
// Jake's spec (cw_011): a football-outline icon on the masthead of every Front
// Office screen, red circle with a number, carrying a variety of notifications
// that take you to different places.
//
// General events read GET /api/notifications; service-delivery timing reads
// GET /api/notifications/service-delivery and renders under its own clipboard.
// General rows support All / Unread / Action needed plus full paged history.
//
// Both bells refresh every 60s, on window focus, and when opened.
//
// `tone` picks the icon colour for the two masthead styles in the app: 'dark'
// masthead (bg-hunter-deep, most pages) and 'light' (Dashboard's canvas).
// =============================================================================

import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api.js';
import { notificationDestination } from '../lib/notification-links.js';
import ServiceDeliveryBell from './ServiceDeliveryBell.jsx';

const POLL_MS = 60_000;

const TYPE_LABEL = {
  enrollment_completed: 'Enrollment',
  payment_declined: 'Payment declined',
  payment_refunded: 'Refund',
  task_completed: 'Call Sheet',
  plan_answers_ready: 'Plan Builder',
  plan_renewal_due: 'Plan renewal',
  flux_info: 'FluxEngine',
  flux_campaign_paused: 'FluxEngine',
  flux_hot_lead: 'FluxEngine',
  flux_coach_reply: 'FluxEngine',
  flux_token_revoked: 'FluxEngine',
};

function relativeTime(iso) {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.round(diff / 60_000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// Football outline. Stroke idiom from Dashboard's `I` map, but sized to the
// masthead tier (28px), not the 20px chip tier — Jake's eyeball on prod
// (cw_015): the red badge was ~2× the ball. The ball must dominate; the badge
// keeps its size and sits on the ball's upper-right corner.
function FootballIcon({ className = 'h-7 w-7' }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M4.5 19.5c-1.6-1.6-1.4-6.1 2.1-9.6S15.9 3.3 19.5 4.5c1.6 1.6 1.4 6.1-2.1 9.6S8.1 21.1 4.5 19.5Z" />
      <path d="M9 15l6-6" /><path d="M10.5 10.5l1 1M12.5 12.5l1 1" />
      <path d="M4.8 12.3c2 .3 4.6 2.9 6.9 6.9M12.3 4.8c.3 2 2.9 4.6 6.9 6.9" />
    </svg>
  );
}

function ReadIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <path d="m8 12 2.5 2.5L16 9" />
    </svg>
  );
}

function GeneralNotificationBell({ tone = 'dark' }) {
  const navigate = useNavigate();
  const [counts, setCounts] = useState({ unread: 0, action: 0, badge: 0 });
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState('all');
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const wrapRef = useRef(null);

  const fetchAll = useCallback(async () => {
    try {
      const d = await api.get(`/api/notifications?filter=${encodeURIComponent(filter)}&limit=30`);
      setCounts({ unread: d.unread_count || 0, action: d.action_needed_count || 0, badge: d.badge_count || 0 });
      setItems(Array.isArray(d.notifications) ? d.notifications : []);
      setError(null);
    } catch (err) {
      // A masthead widget must never take the page down with it.
      setError(err?.body?.error || err?.message || 'Could not load notifications');
    }
  }, [filter]);

  // Count poll: on mount, every 60s, and on window focus.
  useEffect(() => {
    fetchAll();
    const id = setInterval(fetchAll, POLL_MS);
    const onFocus = () => fetchAll();
    window.addEventListener('focus', onFocus);
    return () => { clearInterval(id); window.removeEventListener('focus', onFocus); };
  }, [fetchAll]);

  // Fresh list on open.
  useEffect(() => {
    if (!open) return;
    setLoading(true);
    fetchAll().finally(() => setLoading(false));
  }, [open, fetchAll]);

  // Click-away + Escape close.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);

  async function openItem(n) {
    setOpen(false);
    markOne(n);
    const destination = notificationDestination(n);
    if (destination) navigate(destination);
  }

  function markOne(n) {
    if (!n.read_at) {
      setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, read_at: new Date().toISOString() } : x)));
      setCounts((c) => ({ ...c, unread: Math.max(0, c.unread - 1), badge: Math.max(0, c.badge - 1) }));
      api.post(`/api/notifications/${n.id}/read`, {}).catch(() => fetchAll());
    }
  }

  async function markAll() {
    setItems((prev) => prev.map((x) => (x.read_at ? x : { ...x, read_at: new Date().toISOString() })));
    setCounts((c) => ({ ...c, unread: 0, badge: 0 }));
    try { await api.post('/api/notifications/read-all', {}); } catch { /* refetch below */ }
    fetchAll();
  }

  const iconTone = tone === 'light'
    ? 'text-hunter-deep hover:text-mid-green'
    : 'text-silver-a hover:text-white';

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`relative inline-flex items-center justify-center h-10 w-10 rounded-full transition-colors ${iconTone} focus:outline-none focus-visible:ring-2 focus-visible:ring-mid-green/50`}
        aria-label={counts.badge > 0 ? `${counts.badge} notifications` : 'Notifications'}
        aria-haspopup="true"
        aria-expanded={open}
        title="Notifications"
      >
        <FootballIcon />
        {counts.badge > 0 && (
          <span className="absolute top-0 right-0 min-w-[1.15rem] h-[1.15rem] px-1 rounded-full bg-red-600 text-white text-[0.65rem] font-bold leading-[1.15rem] text-center shadow">
            {counts.badge > 99 ? '99+' : counts.badge}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-[32rem] max-w-[calc(100vw-2rem)] bg-white text-silver-text rounded-lg shadow-xl border border-gray-200 z-50 overflow-hidden"
          role="menu"
          aria-label="Notifications"
        >
          <div className="px-4 py-2.5 border-b border-gray-100 flex items-center justify-between">
            <span className="font-display font-black text-hunter text-base">Notifications</span>
            <span className="text-sm text-silver-mute">{counts.unread} unread</span>
          </div>

          <div className="flex gap-1 border-b border-gray-100 px-3 py-2" role="tablist" aria-label="Notification filter">
            {[
              ['all', 'All'],
              ['unread', `Unread (${counts.unread})`],
              ['action', `Action needed (${counts.action})`],
            ].map(([id, label]) => (
              <button key={id} type="button" role="tab" aria-selected={filter === id} onClick={() => setFilter(id)} className={`rounded-full px-3 py-1.5 text-xs font-bold ${filter === id ? 'bg-hunter text-white' : 'text-hunter hover:bg-silver-a'}`}>
                {label}
              </button>
            ))}
          </div>

          <div className="max-h-[min(70vh,42rem)] overflow-y-auto">
            {error && <div className="px-4 py-3 text-xs text-red-700">{error}</div>}
            {!error && loading && items.length === 0 && (
              <div className="px-4 py-6 text-sm text-silver-mute text-center">Loading…</div>
            )}
            {!error && !loading && items.length === 0 && (
              <div className="px-4 py-6 text-sm text-silver-mute text-center">No notifications in this view.</div>
            )}
            {items.map((n) => {
              const unread = !n.read_at;
              return (
                <div key={n.id} className={`flex items-stretch border-b border-gray-100 last:border-0 ${unread ? 'bg-sage-100/40' : ''}`}>
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => openItem(n)}
                    className="min-w-0 flex flex-1 gap-3 px-4 py-3.5 text-left hover:bg-silver-a/60"
                  >
                    <span className={`mt-2 h-2 w-2 shrink-0 rounded-full ${unread ? 'bg-red-600' : 'bg-transparent'}`} aria-hidden="true" />
                    <span className="min-w-0 flex-1">
                      <span className="block text-base font-semibold leading-snug text-silver-text">{n.title || TYPE_LABEL[n.type] || 'Notification'}</span>
                      {n.body && <span className="mt-0.5 block text-sm leading-snug text-silver-mute line-clamp-2">{n.body}</span>}
                      <span className="mt-1 block text-xs font-semibold uppercase tracking-wide text-silver-mute">
                        {TYPE_LABEL[n.type] || (n.type || 'event').replace(/_/g, ' ')} · {relativeTime(n.created_at)}
                      </span>
                    </span>
                  </button>
                  {unread && (
                    <button
                      type="button"
                      onClick={() => markOne(n)}
                      className="flex w-12 shrink-0 items-center justify-center text-silver-mute hover:bg-silver-a/60 hover:text-mid-green focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-mid-green/50"
                      aria-label={`Mark ${n.title || 'notification'} as read`}
                      title="Mark as read"
                    >
                      <ReadIcon />
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          <div className="flex items-center justify-between border-t border-gray-100 px-4 py-2.5">
            <button type="button" onClick={() => { setOpen(false); navigate(`/notifications?filter=${filter}`); }} className="text-sm font-bold text-hunter hover:underline">
              View all
            </button>
            <button type="button" onClick={markAll} disabled={counts.unread === 0} className="text-xs text-hunter underline disabled:opacity-40 disabled:no-underline">
              Mark all read
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function NotificationBell({ tone = 'dark' }) {
  return (
    <div className="flex items-center gap-1">
      <GeneralNotificationBell tone={tone} />
      <ServiceDeliveryBell tone={tone} />
    </div>
  );
}
